import {
    AdditionalServiceLanguage,
    defaultServiceLanguage,
    type ServiceLanguage,
} from "@albertine/i18n";
import {
    AddressComponent,
    AddressComponents,
    BoundsFilter,
    type CatalogArticle,
    CatalogImage,
    CatalogItemEditables,
    CatalogItemId,
    CatalogItemPreview,
    CatalogItemTranslationDe,
    CatalogItemTranslationFr,
    CatalogLocationFilter,
    type CatalogPrimaryFilters,
    type CatalogQueryFilters,
    type CatalogQuerySecondaryFilters,
    CityGuideLocation,
    GooglePlace,
    OfferingCategory,
    Tag,
    ValidateTag,
} from "@albertine/types";
import {
    type CatalogItemFull,
    type SearchCatalogItemsQuery,
    SuccessResponseGetCatalogItemByIdSchema,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { tagDisplayName, translation } from "albertine-shared-web";
import * as z from "zod";
import { CatalogSourceKey } from "./CatalogCategories";
import { categoryDisplayName } from "./types/OfferingCategory";
import {
    CatalogItemEditableTranslation,
    catalogItemNameMaxLength,
    collectTranslationValues,
} from "./types/Translation";
import { excludeUndefined } from "./utils/array.util";
import { resolveCityGuideLocation } from "./utils/catalog.util";
import { isTypeOf } from "./utils/zod.util";
import {
    AgentId,
    AlbertineServiceAgentId,
    FirebaseId,
    GeoPoint,
    IsoDateTimeString,
    LongString,
    MemberId,
    ShortString,
} from "./validate";

export type AddressFields = {
    administrativeAreaLevel1?: string;
    administrativeAreaLevel2?: string;
    locality?: string;
    sublocalityLevel1?: string;
    neighborhood?: string;
    country?: string;
};

export type {
    CatalogPrimaryFilters,
    CatalogQueryFilters,
    CatalogQuerySecondaryFilters,
};
// Re-export shared types from @albertine/types for backward compatibility
export { AddressComponent, AddressComponents, BoundsFilter, CatalogLocationFilter };

export function addressComponentsToObject(
    addressComponents: AddressComponents | undefined,
): AddressFields | undefined {
    if (addressComponents) {
        const administrativeAreaLevel1 = addressComponents.find((it) =>
            it.types.includes("administrative_area_level_1"),
        )?.longName;
        const administrativeAreaLevel2 = addressComponents.find((it) =>
            it.types.includes("administrative_area_level_2"),
        )?.longName;
        const locality = addressComponents.find((it) =>
            it.types.includes("locality"),
        )?.longName;
        const sublocalityLevel1 = addressComponents.find((it) =>
            it.types.includes("sublocality_level_1"),
        )?.longName;
        const neighborhood = addressComponents.find((it) =>
            it.types.includes("neighborhood"),
        )?.longName;
        const country = addressComponents.find((it) =>
            it.types.includes("country"),
        )?.longName;

        if (country) {
            return {
                administrativeAreaLevel1,
                administrativeAreaLevel2,
                sublocalityLevel1,
                locality,
                neighborhood,
                country,
            };
        }
    }

    return undefined;
}

export function catalogItemToArticleLocation(
    catalogItem: Pick<CatalogItem, "addressComponents" | "geoPoint">,
): Pick<CatalogArticle, "location"> {
    const { addressComponents, geoPoint } = catalogItem;
    const components = addressComponents
        ? addressComponentsToObject(addressComponents)
        : undefined;

    return geoPoint?.latitude && geoPoint?.longitude
        ? {
              location: {
                  point: {
                      lat: geoPoint.latitude,
                      lon: geoPoint.longitude,
                  },
                  administrativeAreaLevel1: components?.administrativeAreaLevel1 || "",
                  administrativeAreaLevel2: components?.administrativeAreaLevel2 || "",
                  locality: components?.locality || "",
                  sublocalityLevel1: components?.sublocalityLevel1 || "",
                  neighborhood: components?.neighborhood || "",
                  country: components?.country || "",
              },
          }
        : { location: undefined };
}

export function catalogItemToIndexDocument(catalogItem: CatalogItem): CatalogArticle {
    const { storyblokArticleSlug: slug } = catalogItem;

    const articleLocation = catalogItemToArticleLocation(catalogItem).location;

    const tags = isTypeOf(catalogItem.tags, z.array(Tag)) ? catalogItem.tags : [];
    const keyDetailDe = catalogItem.keyDetail
        ? getItemKeyDetail(
              catalogItem.offeringCategories,
              "de",
              tags,
              catalogItem.travelItineraryNotesDe,
              catalogItem.eventDatesDe,
              catalogItem.cityGuideLocation,
          )
        : null;
    const keyDetailFr = catalogItem.keyDetail
        ? getItemKeyDetail(
              catalogItem.offeringCategories,
              "fr",
              tags,
              catalogItem.travelItineraryNotesDe,
              catalogItem.eventDatesDe,
              catalogItem.cityGuideLocation,
          )
        : null;

    return {
        id: catalogItem.id,
        googlePlaceId: catalogItem.googlePlaceId,
        name: catalogItem.name,
        description: catalogItem.description || "",
        address: catalogItem.address || "",
        benefits: catalogItem.benefits || null,
        bookingPolicy: catalogItem.bookingPolicy || null,
        eventDates: catalogItem.eventDates || null,
        localInsights: catalogItem.localInsights || "",
        website: catalogItem.website || null,
        offeringCategories: catalogItem.offeringCategories,
        suppliers: catalogItem.suppliers,
        notes: catalogItem.notes || "",
        tags,
        images: catalogItem.images ? catalogItem.images.map((it) => it.url) : undefined,
        source: catalogItem.source || "",
        published: catalogItem.published || false,
        shared: catalogItem.shared || false,
        location: articleLocation,
        createdAt: catalogItem.createdAt,
        albertineVerified: catalogItem.albertineVerified || null,
        recentlyOpened: catalogItem.recentlyOpened || null,
        slug: slug || null,
        cityGuideLocation: catalogItem.cityGuideLocation
            ? catalogItem.cityGuideLocation.key
            : null,
        headline: catalogItem.headline || null,
        travelItineraryNotes: catalogItem.travelItineraryNotes || null,
        translations: {
            de: {
                address: catalogItem.addressDe || null,
                benefits: catalogItem.benefitsDe || null,
                bookingPolicy: catalogItem.bookingPolicyDe || null,
                description: catalogItem.descriptionDe || null,
                eventDates: catalogItem.eventDatesDe || null,
                localInsights: catalogItem.localInsightsDe || null,
                headline: catalogItem.headlineDe || null,
                name: catalogItem.nameDe || null,
                travelItineraryNotes: catalogItem.travelItineraryNotesDe || null,
                keyDetail: keyDetailDe,
            },
            fr: {
                address: catalogItem.addressFr || null,
                benefits: catalogItem.benefitsFr || null,
                bookingPolicy: catalogItem.bookingPolicyFr || null,
                description: catalogItem.descriptionFr || null,
                eventDates: catalogItem.eventDatesFr || null,
                localInsights: catalogItem.localInsightsFr || null,
                headline: catalogItem.headlineFr || null,
                name: catalogItem.nameFr || null,
                travelItineraryNotes: catalogItem.travelItineraryNotesFr || null,
                keyDetail: keyDetailFr,
            },
        },
        keyDetail: catalogItem.keyDetail || null,
        googlePlaceData: catalogItem.googlePlaceData || null,
    };
}

export function moreStrictLocationSearchThanCountry(filters: AddressFields): boolean {
    return !!(
        filters.administrativeAreaLevel1 ||
        filters.administrativeAreaLevel2 ||
        filters.locality ||
        filters.neighborhood ||
        filters.sublocalityLevel1
    );
}

// Re-export CatalogImage, CityGuideLocation from @albertine/types
export { CatalogImage, CityGuideLocation };

export const CatalogItemSharingLogEntry = z.object({
    messageId: FirebaseId,
    memberId: MemberId,
    conversationId: FirebaseId,
    requestId: FirebaseId.nullable(),
    agentId: AgentId.nullable(),
    sentByAgent: z.boolean(),
    sharedAt: IsoDateTimeString,
});
export type CatalogItemSharingLogEntry = z.infer<typeof CatalogItemSharingLogEntry>;

export const CatalogItemSharingLogEntryDbChema = z.object({
    catalog_item_id: z.string(),
    message_id: z.string(),
    member_id: MemberId,
    conversation_id: FirebaseId,
    request_id: FirebaseId.nullable(),
    agent_id: AgentId.nullable(),
    sent_by_agent: z.boolean(),
    shared_at: z.date(),
});

export type CatalogItemSharingLogEntryDbChema = z.infer<
    typeof CatalogItemSharingLogEntryDbChema
>;

// Re-export CatalogItemTranslationDe, CatalogItemTranslationFr, CatalogItemEditables from @albertine/types
export { CatalogItemEditables, CatalogItemTranslationDe, CatalogItemTranslationFr };
export type CatalogItemLanguage =
    | z.infer<typeof CatalogItemTranslationDe>
    | z.infer<typeof CatalogItemTranslationFr>;

export function newCatalogItem(): CatalogItemEditables {
    return {
        name: "",
        images: [],
    };
}

// Re-export CatalogItemId from @albertine/types
export { CatalogItemId };

export const NewCatalogItemPayload = CatalogItemEditables.omit({
    name: true,
}).extend({
    name: z.string(),
});
export type NewCatalogItemPayload = z.infer<typeof NewCatalogItemPayload>;

export const CatalogItemUpdate = CatalogItemEditables.extend({
    updatedAt: IsoDateTimeString,
    updatedBy: AgentId.or(AlbertineServiceAgentId),
});
export type CatalogItemUpdate = z.infer<typeof CatalogItemUpdate>;

export const NewCatalogItem = CatalogItemUpdate.omit({
    name: true,
}).extend({
    name: z.string(),
    createdAt: IsoDateTimeString,
    createdBy: AgentId.or(AlbertineServiceAgentId),
});
export type NewCatalogItem = z.infer<typeof NewCatalogItem>;

export const CatalogItem = NewCatalogItem.extend({
    id: CatalogItemId,
    sharingLog: z.array(CatalogItemSharingLogEntry),
    shared: z.boolean(),
    archived: z.boolean(),
});
export type CatalogItem = z.infer<typeof CatalogItem>;

const minImages = 1;

export const CatalogItemFormSchema = z.object({
    name: z
        .string({ error: "Add name" })
        .min(3, "Add name")
        .max(catalogItemNameMaxLength, "Limit for name is 255 characters"),
    images: z
        .array(CatalogImage, { error: "Add at least one image" })
        .min(minImages, "Add at least one image"),
    description: z
        .string({ error: "Add general description" })
        .min(3, "Add general description"),
    offeringCategory: OfferingCategory,
    localInsights: z.string().optional().nullable(),
    source: CatalogSourceKey,
    notes: z.string().optional().nullable(),
    address: LongString.optional().nullable(),
    website: z.url().optional().nullable(),
    phoneNumber: ShortString.optional().nullable(),
    tags: z.array(ValidateTag).optional().nullable(),
    published: z.boolean(),
    addressComponents: AddressComponents,
    googlePlaceId: z.string().optional().nullable(),
    googlePlaceData: GooglePlace.optional().nullable(),
    googleUrl: z.url().optional().nullable(),
    geoPoint: GeoPoint.optional().nullable(),

    headline: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    bookingPolicy: z.string().optional().nullable(),
    albertineVerified: IsoDateTimeString.optional().nullable(),
    recentlyOpened: IsoDateTimeString.optional().nullable(),
    eventDates: z.string().optional().nullable(),
    travelItineraryNotes: z.string().optional().nullable(),
    discoverableForTenant: TenantSchema.optional().nullable(),

    suppliers: z.array(z.string()).optional(),
    translations: z.partialRecord(
        AdditionalServiceLanguage,
        CatalogItemEditableTranslation.optional(),
    ),
});

export type CatalogItemFormSchema = z.infer<typeof CatalogItemFormSchema>;

// Re-export CatalogItemPreview from @albertine/types
export { CatalogItemPreview };

type Location = {
    lat?: number | (() => number);
    lng?: number | (() => number);
    latitude?: number;
    longitude?: number;
    _lat?: number;
    _long?: number;
    _latitude?: number;
    _longitude?: number;
};

export function safelyConvertLocationToGeoPoint(
    location: Location | undefined | null,
): { latitude: number; longitude: number } | undefined {
    if (!location) return undefined;

    if (typeof location.lat === "function" && typeof location.lng === "function") {
        return {
            latitude: location.lat(),
            longitude: location.lng(),
        };
    }

    if (typeof location.lat === "number" && typeof location.lng === "number") {
        return {
            latitude: location.lat,
            longitude: location.lng,
        };
    }

    if (location._lat && location._long) {
        return {
            latitude: location._lat,
            longitude: location._long,
        };
    }

    if (location._latitude && location._longitude) {
        return {
            latitude: location._latitude,
            longitude: location._longitude,
        };
    }

    if (location.latitude && location.longitude) {
        return {
            latitude: location.latitude,
            longitude: location.longitude,
        };
    }

    return { latitude: 0, longitude: 0 };
}

function requiredFieldIsNotEmpty(requiredField: string | null | undefined) {
    return (requiredField || "")?.length >= 3;
}

export function hasWoaRequiredFields(
    catalogItem:
        | CatalogItem
        | NewCatalogItemPayload
        | CatalogItemEditables
        | CatalogItemFull,
): boolean {
    /* TODO: Switch to requiring primary category when we actually store them */
    const { name, images, description, offeringCategories } = catalogItem;
    const hasAtLeastOneImage = (images || []).length >= 1;
    const atLeastOneCategory = (offeringCategories || []).length > 0;

    return (
        requiredFieldIsNotEmpty(name) &&
        hasAtLeastOneImage &&
        requiredFieldIsNotEmpty(description) &&
        atLeastOneCategory
    );
}

export function hasTranslations(
    language: AdditionalServiceLanguage | "all",
    catalogItem: CatalogItem | NewCatalogItemPayload | CatalogItemEditables,
): boolean {
    const properties = collectTranslationValues(
        ["name", "description"],
        catalogItem,
        language,
    );

    return properties.filter(excludeUndefined).length === properties.length;
}

function catalogItemFullHasTranslations(
    language: AdditionalServiceLanguage | "all",
    catalogItem: CatalogItemFull,
): boolean {
    const languages =
        language === "all" ? AdditionalServiceLanguage.options : [language];
    for (const lang of languages) {
        const translationsForLang = catalogItem.translations[lang];
        if (!translationsForLang) {
            return false;
        }

        if (
            translationsForLang.name === null ||
            translationsForLang.description === null
        ) {
            return false;
        }
    }

    return true;
}

export function canBeSharedAsProactivity(
    catalogItem: CatalogItemFull,
    serviceLanguage: ServiceLanguage,
) {
    if (serviceLanguage !== defaultServiceLanguage) {
        return (
            catalogItemFullHasTranslations(serviceLanguage, catalogItem) &&
            hasWoaRequiredFields(catalogItem)
        );
    }
    return hasWoaRequiredFields(catalogItem);
}

export function canBeUsedForBooking(
    catalogItem: CatalogItemFull,
    serviceLanguage: ServiceLanguage,
) {
    if (serviceLanguage !== defaultServiceLanguage) {
        return (
            catalogItemFullHasTranslations(serviceLanguage, catalogItem) &&
            hasWoaRequiredFields(catalogItem)
        );
    }
    return hasWoaRequiredFields(catalogItem);
}

export function catalogItemToCatalogItemPreview(
    catalogItem: CatalogItemFull,
    serviceLanguage: ServiceLanguage,
): CatalogItemPreview {
    const translations =
        serviceLanguage === "en"
            ? catalogItem
            : catalogItem.translations[serviceLanguage];

    const name = translations.name;

    const keyDetail = getCatalogItemKeyDetailForLanguage(catalogItem, serviceLanguage);
    return {
        id: catalogItem.id,
        name: name ?? undefined,
        keyDetail,
        image: catalogItem.images?.[0] ? catalogItem.images[0].url : undefined,
    };
}

export function getItemKeyDetailLocation(
    location: CityGuideLocation | null | undefined,
    serviceLanguage: ServiceLanguage,
): string | null {
    return location?.city
        ? translation(`key_detail.cityguide:${location.key}`, {
              lng: serviceLanguage,
              defaultValue: location.city,
          })
        : null;
}

function getDiningKeyDetail(tags: Tag[], serviceLanguage: ServiceLanguage) {
    const cuisineTag = tags
        .sort((t1, t2) => {
            if (t1 === "cuisine:asian") return 1;
            if (t2 === "cuisine:asian") return -1;
            return t1.localeCompare(t2);
        })
        .find((tag: Tag) => tag.startsWith("cuisine:"));
    return cuisineTag
        ? translation(`key_detail.${cuisineTag}`, { lng: serviceLanguage })
        : translation("key_detail.restaurant", { lng: serviceLanguage });
}

function getTypesOrCategory(
    tags: Tag[],
    category: OfferingCategory,
    serviceLanguage: ServiceLanguage,
) {
    const types = tags
        .filter((tag: Tag) => tag.startsWith("type:"))
        .slice(0, 3)
        .map((tag: Tag) => tagDisplayName(tag, serviceLanguage))
        .join(", ");
    return types || categoryDisplayName(category, serviceLanguage);
}
export function getItemKeyDetailDescription(
    categories: OfferingCategory[] | null | undefined,
    serviceLanguage: ServiceLanguage,
    tags: Tag[] = [],
    travelItineraryNotes: string | null | undefined = null,
    eventDates: string | null | undefined = null,
): string | null {
    if (!categories || categories.length === 0) {
        return null;
    }
    const category = categories[0];

    switch (category) {
        case "dining":
            return getDiningKeyDetail(tags, serviceLanguage);
        case "accommodation":
        case "nightlife":
        case "localExperience":
        case "wellness":
            return getTypesOrCategory(tags, category, serviceLanguage);
        case "travelItinerary":
            return (
                travelItineraryNotes || categoryDisplayName(category, serviceLanguage)
            );
        case "event":
            return eventDates || categoryDisplayName(category, serviceLanguage);
        default:
            return categoryDisplayName(category, serviceLanguage);
    }
}

export function getCatalogItemKeyDetail(
    catalogItem: Partial<
        Pick<
            CatalogItemFull,
            | "offeringCategories"
            | "tags"
            | "travelItineraryNotes"
            | "eventDates"
            | "addressComponents"
        >
    >,
): string | null {
    return getCatalogItemKeyDetailForLanguage(catalogItem, "en");
}

export function getCatalogItemKeyDetailForLanguage(
    catalogItem: Partial<
        Pick<
            CatalogItemFull,
            | "offeringCategories"
            | "tags"
            | "travelItineraryNotes"
            | "eventDates"
            | "addressComponents"
        >
    >,
    serviceLanguage: ServiceLanguage,
): string | null {
    const cityGuideLocation = resolveCityGuideLocation(catalogItem.addressComponents);
    return getItemKeyDetail(
        catalogItem.offeringCategories,
        serviceLanguage,
        catalogItem.tags,
        catalogItem.travelItineraryNotes,
        catalogItem.eventDates,
        cityGuideLocation,
    );
}

export function getItemKeyDetail(
    categories: OfferingCategory[] | null | undefined,
    serviceLanguage: ServiceLanguage,
    tags: Tag[] = [],
    travelItineraryNotes: string | null | undefined = null,
    eventDates: string | null | undefined = null,
    location: CityGuideLocation | null | undefined = null,
): string | null {
    const result = [
        getItemKeyDetailDescription(
            categories,
            serviceLanguage,
            tags,
            travelItineraryNotes,
            eventDates,
        ),
        getItemKeyDetailLocation(location, serviceLanguage),
    ]
        .filter(excludeUndefined)
        .join(" · ");
    return result === "" ? null : result;
}

export function toCatalogItemsSearchGetParams(filter: SearchCatalogItemsQuery) {
    return {
        query: {
            ...filter,
            categories: filter.categories ?? [],
            ids: filter.ids ?? [],
            supplierLmtIds: filter.supplierLmtIds ?? [],
            sources: filter.sources ?? [],
            tags: filter.tags ?? [],
            latitude: filter.latitude ? filter.latitude.toString() : undefined,
            longitude: filter.longitude ? filter.longitude.toString() : undefined,
            maxGeoDistanceKm: filter.maxGeoDistanceKm
                ? filter.maxGeoDistanceKm.toString()
                : undefined,
            geoBoundingBoxTop: filter.geoBoundingBoxTop
                ? filter.geoBoundingBoxTop.toString()
                : undefined,
            geoBoundingBoxBottom: filter.geoBoundingBoxBottom
                ? filter.geoBoundingBoxBottom.toString()
                : undefined,
            geoBoundingBoxLeft: filter.geoBoundingBoxLeft
                ? filter.geoBoundingBoxLeft.toString()
                : undefined,
            geoBoundingBoxRight: filter.geoBoundingBoxRight
                ? filter.geoBoundingBoxRight.toString()
                : undefined,
            pageSize: filter.pageSize ? filter.pageSize.toString() : undefined,
        },
    };
}

export function hasAllBoundingBoxParams(
    filter: SearchCatalogItemsQuery,
): filter is SearchCatalogItemsQuery & {
    geoBoundingBoxTop: number;
    geoBoundingBoxBottom: number;
    geoBoundingBoxLeft: number;
    geoBoundingBoxRight: number;
} {
    return (
        filter.geoBoundingBoxTop !== undefined &&
        filter.geoBoundingBoxBottom !== undefined &&
        filter.geoBoundingBoxLeft !== undefined &&
        filter.geoBoundingBoxRight !== undefined
    );
}

export function hasAllLocationParams(filter: SearchCatalogItemsQuery): boolean {
    return (
        filter.latitude !== undefined &&
        filter.longitude !== undefined &&
        filter.maxGeoDistanceKm !== undefined
    );
}

type JsonResponse = { json(): Promise<unknown> };
export type ApiGetEndpoint<TParams> = (
    params: TParams,
) => Promise<Promise<JsonResponse>>;
type ApiClientForGetCatalogItemById = {
    get: {
        catalogItemById: ApiGetEndpoint<{ param: { id: string } }>;
    };
};
export async function findCatalogItemById(
    apiClient: ApiClientForGetCatalogItemById,
    id: string,
): Promise<CatalogItemFull | undefined> {
    // Note: await twice because withAuth wraps with another Promise
    const response = await apiClient.get.catalogItemById({ param: { id } });
    const responseJson: unknown = await response.json();
    const parsed = SuccessResponseGetCatalogItemByIdSchema.safeParse(responseJson);
    if (parsed.success) {
        return parsed.data.data.catalogItem;
    }
    return undefined;
}
